import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ArrowBackRounded,
  CancelOutlined,
  EventBusyRounded,
  PublishedWithChangesRounded,
  TaskAlt,
  WarningAmberRounded,
} from "@mui/icons-material";
import { TASKS_API_BASE } from "./apiConfig";

const CURRENT_ERROR_WHITELIST = {
  dateMismatches: new Set([]),
  missingInScadentar: new Set([
    "10530",
    "10492",
    "10473",
    "10460",
    "10582",
    "10585",
    "10593",
    "10594",
    "10609",
    "10599",
    "10577",
    "10602",
    "10606",
    "10604",
    "10607",
    "10597",
    "10158",
    "10182",
    "12334",
    "13341",
    "13344",
    "14305",
    "10502",
    "14323",
    "10752",
    "10336",
    "14551",
    "12264",
    "12459",
    "14552",
  ]),
  missingInApp: new Set([
    "LEB-11785",
    "LEB-10674",
    "LEB-11174",
    "LEB-11052",
    "LEB-9828",
    "LEB-10834",
    "LEB-9810",
    "LEB-10797",
    "LEB-10735",
    "LEB-10274",
    "LEB-10676",
    "LEB-9949",
    "LEB-10673",
    "LEB-2133",
    "LEB-8843",
    "LEB-4005",
    "LEB-9824",
    "LEB-9823",
    "LEB-5445",
    "LEB-8838",
    "LEB-6933",
    "LEB-7771",
    "LEB-8547",
    "LEB-9820",
    "LEB-9808",
    "LEB-8950",
    "LEB-44",
    "LEB-2789",
    "LEB-6510",
    "LEB-4015",
    "LEB-4009",
    "LEB-4007",
    "LEB-4018",
    "LEB-4042",
    "LEB-4003",
    "LEB-4024",
    "LEB-4014",
    "LEB-4057",
    "LEB-4039",
    "LEB-4026",
    "LEB-4021",
    "LEB-4067",
    "LEB-4056",
    "LEB-4037",
    "LEB-4011",
    "LEB-4048",
    "LEB-4066",
    "LEB-4061",
    "LEB-4047",
    "LEB-4045",
    "LEB-6652",
    "LEB-3098",
    "LEB-4948",
    "LEB-8834",
    "LEB-7880",
    "LEB-9780",
    "LEB-7190",
    "LEB-9952",
    "LEB-9822",
    "LEB-9821",
    "LEB-9809",
    "LEB-4443",
  ]),
  duplicateRemote: new Set([]),
  duplicateLocal: new Set([]),
};

const getWhitelistKey = (category, item = {}) => {
  const valueByCategory = {
    dateMismatches: item.productId,
    missingInScadentar: item.productId || item.rawProductId,
    missingInApp: item.scadentarKey || item.productId || item.issueId,
    duplicateRemote: item.productId || item.rawProductId,
    duplicateLocal: item.scadentarKey || item.productId || item.issueId,
  };

  const value = valueByCategory[category];
  return value === undefined || value === null ? "" : String(value);
};

const isWhitelisted = (category, item) =>
  Boolean(CURRENT_ERROR_WHITELIST[category]?.has(getWhitelistKey(category, item)));

const StatCard = ({ title, value, tone = "neutral" }) => {
  const tones = {
    neutral: {
      border: "rgba(148,163,184,0.25)",
      background: "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.94))",
      color: "#0f172a",
    },
    success: {
      border: "rgba(16,185,129,0.26)",
      background: "linear-gradient(135deg, rgba(236,253,245,0.96), rgba(209,250,229,0.9))",
      color: "#065f46",
    },
    danger: {
      border: "rgba(244,63,94,0.26)",
      background: "linear-gradient(135deg, rgba(255,241,242,0.98), rgba(255,228,230,0.92))",
      color: "#9f1239",
    },
    accent: {
      border: "rgba(59,130,246,0.22)",
      background: "linear-gradient(135deg, rgba(239,246,255,0.98), rgba(219,234,254,0.92))",
      color: "#1d4ed8",
    },
  };

  const style = tones[tone] || tones.neutral;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid",
        minWidth: 180,
        ...style,
      }}
    >
      <Typography
        variant="overline"
        sx={{ display: "block", opacity: 0.8, letterSpacing: "0.08em" }}
      >
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const formatAmount = (value, currency) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  const safeCurrency = currency || "RON";
  try {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return `${value} ${safeCurrency}`;
  }
};

const CompareRow = ({
  title,
  subtitle,
  localDate,
  remoteDate,
  remoteAmount,
  remoteCurrency,
  tone = "neutral",
  whitelisted = false,
}) => {
  const borderColor =
    tone === "danger"
      ? "rgba(244,63,94,0.2)"
      : tone === "warning"
        ? "rgba(245,158,11,0.24)"
        : "rgba(148,163,184,0.18)";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor,
        background: "rgba(255,255,255,0.88)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
          {whitelisted && (
            <Chip
              label="Whitelist"
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(148,163,184,0.12)",
                color: "#475569",
                fontWeight: 700,
              }}
            />
          )}
          {localDate !== undefined && (
            <Chip
              label={`Scadentar: ${localDate || "-"}`}
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(15,23,42,0.06)",
                color: "#111827",
                fontWeight: 600,
              }}
            />
          )}
          {remoteDate !== undefined && (
            <Chip
              label={`APP: ${remoteDate || "-"}`}
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(59,130,246,0.08)",
                color: "#1d4ed8",
                fontWeight: 600,
              }}
            />
          )}
          {remoteAmount !== undefined && (
            <Chip
              label={`Sumă APP: ${formatAmount(remoteAmount, remoteCurrency)}`}
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(16,185,129,0.08)",
                color: "#047857",
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const Section = ({ title, count, icon, children, tone = "neutral" }) => {
  const colors = {
    neutral: "#475569",
    success: "#047857",
    warning: "#b45309",
    danger: "#be123c",
  };
  const color = colors[tone] || colors.neutral;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 5,
        border: "1px solid rgba(226,232,240,0.9)",
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827" }}>
            {title}
          </Typography>
        </Stack>
        <Chip
          label={count}
          sx={{
            fontWeight: 700,
            color,
            backgroundColor: "rgba(255,255,255,0.9)",
          }}
        />
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={1.5}>{children}</Stack>
    </Paper>
  );
};

const ScadentarPaymentPlannedCheckPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showWhitelist, setShowWhitelist] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${TASKS_API_BASE}/scadentar/payment-planned-check`
        );
        if (!cancelled) {
          setData(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              "Nu s-a putut încărca reconcilierea dintre APP și scadentar."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const summary = data?.summary || {};
  const checks = data?.checks || {};
  const whitelistView = useMemo(() => {
    const categories = [
      "dateMismatches",
      "missingInScadentar",
      "missingInApp",
      "duplicateRemote",
      "duplicateLocal",
    ];

    const visibleChecks = {};
    const hiddenCounts = {};

    categories.forEach((category) => {
      const rows = Array.isArray(checks[category]) ? checks[category] : [];
      const whitelistedRows = rows.filter((item) => isWhitelisted(category, item));
      visibleChecks[category] = showWhitelist
        ? rows
        : rows.filter((item) => !isWhitelisted(category, item));
      hiddenCounts[category] = whitelistedRows.length;
    });

    const visibleProblemRowsCount = categories.reduce(
      (total, category) => total + visibleChecks[category].length,
      0
    );
    const hiddenRowsCount = categories.reduce(
      (total, category) => total + hiddenCounts[category],
      0
    );

    return {
      checks: visibleChecks,
      hiddenCounts,
      visibleProblemRowsCount,
      hiddenRowsCount,
    };
  }, [checks, showWhitelist]);

  const renderEmptyState = (message, hiddenCount) => {
    if (!showWhitelist && hiddenCount > 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          {hiddenCount === 1
            ? "1 rând este în whitelist și este ascuns."
            : `${hiddenCount} rânduri sunt în whitelist și sunt ascunse.`}
        </Alert>
      );
    }

    return (
      <Alert severity="success" sx={{ borderRadius: 3 }}>
        {message}
      </Alert>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(251,191,36,0.16), transparent 26%), radial-gradient(circle at top right, rgba(59,130,246,0.14), transparent 28%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Stack spacing={3} maxWidth={1480} mx="auto">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 6,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.92))",
            color: "#f8fafc",
            boxShadow: "0 28px 80px rgba(15,23,42,0.22)",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            spacing={3}
          >
            <Box>
              <Button
                startIcon={<ArrowBackRounded />}
                onClick={() => navigate("/")}
                sx={{
                  mb: 2,
                  color: "#e2e8f0",
                  borderColor: "rgba(255,255,255,0.18)",
                  textTransform: "none",
                }}
                variant="outlined"
              >
                Înapoi la scadentar
              </Button>
              <Typography variant="overline" sx={{ letterSpacing: "0.12em", opacity: 0.75 }}>
                Payment Planned Reconciliation
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 900, mt: 1, mb: 1.5 }}>
                Verificare APP vs Scadentar
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 760, color: "#cbd5e1" }}>
                Comparăm produsele aflate în status <strong>payment</strong> din APP
                cu intrările locale din scadentar aflate în <strong>Planificat</strong>,
                folosind `productId` din APP și `nr_jurnal` din scadentar, normalizate doar pe cifre.
              </Typography>
            </Box>
            <Stack alignItems={{ xs: "flex-start", lg: "flex-end" }} spacing={1}>
              <Chip
                icon={
                  whitelistView.visibleProblemRowsCount === 0 ? (
                    <TaskAlt />
                  ) : (
                    <CancelOutlined />
                  )
                }
                label={
                  whitelistView.visibleProblemRowsCount === 0
                    ? "Fără diferențe vizibile"
                    : "Necesită verificare"
                }
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  backgroundColor: whitelistView.visibleProblemRowsCount === 0
                    ? "rgba(16,185,129,0.22)"
                    : "rgba(244,63,94,0.22)",
                }}
              />
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Sursă: {data?.source?.url || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Actualizat: {data?.fetchedAt || "-"}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              background: "rgba(255,255,255,0.75)",
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontWeight: 600 }}>
              Verific reconcilierea dintre APP și scadentar...
            </Typography>
          </Paper>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 4 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
              <StatCard
                title="Stare"
                value={summary.healthy ? "OK" : "Atenție"}
                tone={summary.healthy ? "success" : "danger"}
              />
              <StatCard title="Produse APP" value={summary.remoteCount || 0} tone="accent" />
              <StatCard title="Planificate Local" value={summary.localCount || 0} />
              <StatCard
                title="Potriviri"
                value={summary.matchedCount || 0}
                tone="success"
              />
              <StatCard
                title="Erori vizibile"
                value={whitelistView.visibleProblemRowsCount}
                tone={whitelistView.visibleProblemRowsCount ? "danger" : "success"}
              />
              <StatCard
                title="Whitelist"
                value={whitelistView.hiddenRowsCount}
                tone={whitelistView.hiddenRowsCount ? "accent" : "neutral"}
              />
            </Stack>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                border: "1px solid rgba(226,232,240,0.9)",
                background: "rgba(255,255,255,0.82)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#111827" }}>
                    Whitelist erori existente
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {showWhitelist
                      ? "Randurile whitelistate sunt afișate și marcate."
                      : "Randurile whitelistate sunt ascunse."}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showWhitelist}
                      onChange={(event) => setShowWhitelist(event.target.checked)}
                    />
                  }
                  label="Show whitelist"
                  sx={{ m: 0 }}
                />
              </Stack>
            </Paper>

            <Section
              title="Diferențe de dată"
              count={whitelistView.checks.dateMismatches.length}
              icon={<PublishedWithChangesRounded />}
              tone={whitelistView.checks.dateMismatches.length ? "warning" : "success"}
            >
              {whitelistView.checks.dateMismatches.length ? (
                whitelistView.checks.dateMismatches.map((item) => (
                  <CompareRow
                    key={`date-${item.productId}`}
                    title={`Jurnal / Product ID ${item.productId}`}
                    subtitle={item.local?.productName || item.remote?.firstName || "-"}
                    localDate={item.local?.paymentDate}
                    remoteDate={item.remote?.paymentDate}
                    remoteAmount={item.remote?.plannedAmount}
                    remoteCurrency={item.remote?.currency}
                    tone="warning"
                    whitelisted={isWhitelisted("dateMismatches", item)}
                  />
                ))
              ) : (
                renderEmptyState(
                  "Nu există diferențe de dată între APP și scadentar.",
                  whitelistView.hiddenCounts.dateMismatches
                )
              )}
            </Section>

            <Section
              title="Există în APP, lipsesc în scadentar"
              count={whitelistView.checks.missingInScadentar.length}
              icon={<EventBusyRounded />}
              tone={whitelistView.checks.missingInScadentar.length ? "danger" : "success"}
            >
              {whitelistView.checks.missingInScadentar.length ? (
                whitelistView.checks.missingInScadentar.map((item) => (
                  <CompareRow
                    key={`app-${item.rawProductId || item.productId}`}
                    title={`Product ID ${item.rawProductId || item.productId}`}
                    subtitle={`${item.firstName || ""} ${item.lastName || ""}`.trim() || item.email || "-"}
                    remoteDate={item.paymentDate}
                    remoteAmount={item.plannedAmount}
                    remoteCurrency={item.currency}
                    tone="danger"
                    whitelisted={isWhitelisted("missingInScadentar", item)}
                  />
                ))
              ) : (
                renderEmptyState(
                  "Toate produsele planificate în APP există și în scadentar.",
                  whitelistView.hiddenCounts.missingInScadentar
                )
              )}
            </Section>

            <Section
              title="Există în scadentar, lipsesc în APP"
              count={whitelistView.checks.missingInApp.length}
              icon={<WarningAmberRounded />}
              tone={whitelistView.checks.missingInApp.length ? "danger" : "success"}
            >
              {whitelistView.checks.missingInApp.length ? (
                whitelistView.checks.missingInApp.map((item) => (
                  <CompareRow
                    key={`local-${item.scadentarKey || item.productId}`}
                    title={`Jurnal ${item.nrJurnal || item.productId}`}
                    subtitle={item.productName || item.sellerName || "-"}
                    localDate={item.paymentDate}
                    tone="danger"
                    whitelisted={isWhitelisted("missingInApp", item)}
                  />
                ))
              ) : (
                renderEmptyState(
                  "Toate produsele planificate local există și în APP.",
                  whitelistView.hiddenCounts.missingInApp
                )
              )}
            </Section>

            <Section
              title="Duplicate detectate"
              count={
                whitelistView.checks.duplicateRemote.length +
                whitelistView.checks.duplicateLocal.length
              }
              icon={<CancelOutlined />}
              tone={
                whitelistView.checks.duplicateRemote.length +
                  whitelistView.checks.duplicateLocal.length
                  ? "warning"
                  : "success"
              }
            >
              {whitelistView.checks.duplicateRemote.length === 0 &&
              whitelistView.checks.duplicateLocal.length === 0 ? (
                renderEmptyState(
                  "Nu au fost detectate duplicate nici în APP, nici în scadentar.",
                  whitelistView.hiddenCounts.duplicateRemote +
                    whitelistView.hiddenCounts.duplicateLocal
                )
              ) : (
                <>
                  {whitelistView.checks.duplicateRemote.map((item, index) => (
                    <CompareRow
                      key={`dup-remote-${index}`}
                      title={`Duplicat APP ${item.rawProductId || item.productId || "-"}`}
                      subtitle={`${item.firstName || ""} ${item.lastName || ""}`.trim() || item.email || "-"}
                      remoteDate={item.paymentDate}
                      remoteAmount={item.plannedAmount}
                      remoteCurrency={item.currency}
                      tone="warning"
                      whitelisted={isWhitelisted("duplicateRemote", item)}
                    />
                  ))}
                  {whitelistView.checks.duplicateLocal.map((item, index) => (
                    <CompareRow
                      key={`dup-local-${index}`}
                      title={`Duplicat scadentar ${item.nrJurnal || item.productId || "-"}`}
                      subtitle={item.productName || item.sellerName || "-"}
                      localDate={item.paymentDate}
                      tone="warning"
                      whitelisted={isWhitelisted("duplicateLocal", item)}
                    />
                  ))}
                </>
              )}
            </Section>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ScadentarPaymentPlannedCheckPage;
